import React, { useReducer, Reducer } from "react";
import {
	FailureActionType,
	IVRFlowTypeEnum,
	WidgetTypeEnum,
} from "../common/enum";
import { INextActionWidget } from "../common/interfaces";
import { assertUnreachable } from "../common/shared";
import IVRFlowConfigurationModel from "../models/RequestForHelpTemplates/IVRFlowConfigurationModel";
import PickLanguageWidgetModel from "../models/RequestForHelpTemplates/PickLanguageWidgetModel";
import ResponseMapModel from "../models/RequestForHelpTemplates/ResponseMapModel";
import { PickLanguageWidget } from "./../components/RequestForHelpTemplates/PickLanguageWidget/PickLanguageWidget";
import {
	AskQuestionType,
	AskQuestionWidget,
} from "./../components/RequestForHelpTemplates/AskQuestionWidget/AskQuestionWidget";
import { LogRequestForHelpWidget } from "./../components/RequestForHelpTemplates/LogRequestForHelpWidget/LogRequestForHelpWidget";
import { AudioFileDetailModel } from "../models/RequestForHelpTemplates/UploadAudioFileRequestModel";
import { PreSubmissionConfigurationModel } from "../models/BatchMessage/BatchMessageDetailModel";
import AudioFileModel from "../models/RequestForHelpTemplates/AudioFileModel";
import LogRequestForHelpWidgetModel from "../models/RequestForHelpTemplates/LogRequestForHelpWidgetModel";
import WidgetLanguageDetailModel from "../models/RequestForHelpTemplates/WidgetLanguageDetailModel";
import {
	SendMessageWidget,
	SendMessageType,
} from "../components/RequestForHelpTemplates/SendMessageWidget/SendMessageWidget";
import SendMessageWidgetModel from "../models/RequestForHelpTemplates/SendMessageWidgetModel";
import AskQuestionWidgetModel from "../models/RequestForHelpTemplates/AskQuestionWidgetModel";

export const GetNextActionWidgetList = (
	stepperContentList: IVRFlowStepContent[],
	selectedWidgetId: string = ""
) => {
	const createNewWidgetId = "CreateNewWidget";
	const filteredStepperContentList = stepperContentList.filter(
		(s) =>
			s.type !== WidgetTypeEnum.LogRequestForHelp &&
			s.type !== WidgetTypeEnum.PickLanguage &&
			s.id !== selectedWidgetId
	);
	const widgetList: INextActionWidget[] = [];
	filteredStepperContentList.forEach((element) => {
		widgetList.push({ id: element.id, title: element.title });
	});
	// Add LogRequestForHelp widget option
	const index = stepperContentList.findIndex(
		(s) => s.type === WidgetTypeEnum.LogRequestForHelp
	);
	if (index > -1) {
		widgetList.unshift({
			id: stepperContentList[index].id,
			title: stepperContentList[index].title,
			isCustomMenu: true,
		});
	}
	// Add Create New Widget option
	widgetList.unshift({
		id: createNewWidgetId,
		title: "Create New Widget",
		isCustomMenu: true,
	});
	return widgetList;
};

const isBase64String = (value: string): boolean => {
	const validationRegex = /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/;
	return validationRegex.test(value);
};

const getWidgetProperty = (key: any, value: any) => {
	const result = Object.keys(key).find((k) => {
		return key[k].length > 0 ? key[k].includes(value) : key[k] === value;
	});
	return result ? result : "";
};

export const GetAudioFileList = (
	flowConfigurationDetail: PreSubmissionConfigurationModel
): AudioFileDetailModel[] => {
	if (flowConfigurationDetail.id && flowConfigurationDetail?.partitionKey) {
		const audioFileList: AudioFileDetailModel[] = [];
		const flowConfigurationDetailTemp = { ...flowConfigurationDetail };
		// get values from ivrFlowJson property
		let ivrFlowJsonValue = Object.values(
			flowConfigurationDetailTemp.ivrFlowJson
		);
		ivrFlowJsonValue = [].concat(...ivrFlowJsonValue);
		ivrFlowJsonValue.forEach((widget) => {
			if (widget && typeof widget === "object") {
				// get values of selected widget
				let widgetAudioFiles: any = Object.values(widget);
				widgetAudioFiles = [].concat(...widgetAudioFiles);
				// filter undefined or null value from array
				widgetAudioFiles = widgetAudioFiles.filter((el: any) => !!el);

				// filter audio file object with base64 string
				// loop through filtered value and push into audioFileList array
				(widgetAudioFiles.filter(
					(audioFileObject: any) =>
						typeof audioFileObject === "object" &&
						audioFileObject.hasOwnProperty("value") &&
						audioFileObject.value &&
						isBase64String(audioFileObject.value)
				) as AudioFileModel[]).forEach((fileDetail: AudioFileModel) => {
					const audioFileDetailModel: AudioFileDetailModel = new AudioFileDetailModel();
					audioFileDetailModel.language = fileDetail.language || "";
					audioFileDetailModel.audioFile = fileDetail.value;
					audioFileDetailModel.fileName = fileDetail.fileName;
					audioFileDetailModel.widgetProperty = getWidgetProperty(
						widget,
						fileDetail
					);
					audioFileDetailModel.id = widget.id;
					audioFileList.push(audioFileDetailModel);
				});
			}
		});
		return audioFileList;
	}
	return [];
};

export const GetSelectedLanguageList = (
	pickLanguageWidgetDetail: PickLanguageWidgetModel | null
) => {
	const langList: string[] = pickLanguageWidgetDetail
		? pickLanguageWidgetDetail.responseMap
				.map((r) => r.variableValue)
				.filter((lang) => lang !== "")
		: [];
	return Array.from(new Set(langList));
};

export const IsAudioConfigurationAllowed = (
	ivrFlowType: IVRFlowTypeEnum | string
) => {
	return (
		ivrFlowType === IVRFlowTypeEnum.Voice ||
		ivrFlowType === IVRFlowTypeEnum.WhatsApp
	);
};

const widgetItemsToBeValidated = [
	["messageText", "timeout", "responseMap", "failureActionType", "nextAction"],
	[
		"label",
		"variableName",
		"timeout",
		"responseMap",
		"failureActionType",
		"questionText",
		"failureMessage",
		"nextAction",
	],
	["label", "messageText"],
];

const validateWidgetDetail = (
	widget: any,
	propertyName: string,
	ivrFlowType: string,
	selectedLanguageList: string[]
): boolean => {
	if (
		typeof widget[propertyName] === "string" ||
		typeof widget[propertyName] === "number"
	) {
		let isValueExists = widget[propertyName] ? true : false;
		if (propertyName === "nextAction") {
			isValueExists =
				!widget.responseMap.length ||
				widget.responseMap.filter((r: any) => r.nextAction === "").length > 0
					? isValueExists
						? true
						: false
					: true;
		}
		return propertyName === "timeout"
			? ivrFlowType === IVRFlowTypeEnum.USSD
				? true
				: isValueExists
			: isValueExists;
	} else {
		if (propertyName === "responseMap") {
			if (widget[propertyName].length > 0) {
				return widget[propertyName].filter(
					(el: ResponseMapModel) =>
						el.comparison === "" || el.value === "" || el.variableValue === ""
				).length > 0
					? false
					: true;
			}
		} else if (
			propertyName.toString() === "failureDefaultValue" ||
			propertyName.toString() === "failureMessage"
		) {
			if (widget["failureActionType"] === FailureActionType.UseDefaultValue) {
				return widget.failureDefaultValue ? true : false;
			} else if (
				widget["failureActionType"] === FailureActionType.SendMessageAndExit
			) {
				return typeof widget.failureMessage === "string"
					? widget.failureMessage
						? true
						: false
					: widget.failureMessage.length >= selectedLanguageList.length
					? widget.failureMessage.filter((m: any) => m.value === "").length > 0
						? false
						: true
					: false;
			}
		} else {
			return !widget[propertyName].length
				? false
				: widget[propertyName].length >= selectedLanguageList.length
				? widget[propertyName].filter((m: any) => m.value === "").length > 0
					? false
					: true
				: false;
		}
	}
	return true;
};

const checkIsWidgetDetailValid = (
	widget: any,
	ivrFlowType: string,
	selectedLanguageList: string[],
	widgetItems: string[]
) => {
	let isValid = true;
	for (let i = 0; i < widgetItems.length; i++) {
		isValid = validateWidgetDetail(
			widget,
			widgetItems[i],
			ivrFlowType,
			selectedLanguageList
		);
		if (!isValid) {
			break;
		}
	}
	return isValid;
};

export const ValidateIVRFlowDetail = (
	flowConfigurationDetail: PreSubmissionConfigurationModel
) => {
	if (flowConfigurationDetail) {
		if (
			!flowConfigurationDetail.ivrFlowType ||
			!flowConfigurationDetail.title ||
			!flowConfigurationDetail.country
		) {
			return { isValid: false, widgetId: "" };
		} else {
			if (typeof flowConfigurationDetail.ivrFlowJson === "object") {
				let selectedLanguageList = GetSelectedLanguageList(
					flowConfigurationDetail.ivrFlowJson.pickLanguageWidgetDetail
				);
				let widgetList = Object.values(flowConfigurationDetail.ivrFlowJson);
				widgetList = [].concat(...widgetList).filter((el) => !!el);
				let isValid: boolean = true;
				if (widgetList.length > 0) {
					widgetList = widgetList.sort((a, b) => a.orderId - b.orderId);
					for (const widget of widgetList) {
						switch (widget.type) {
							case WidgetTypeEnum.PickLanguage:
								isValid = checkIsWidgetDetailValid(
									widget,
									flowConfigurationDetail.ivrFlowType,
									selectedLanguageList,
									widgetItemsToBeValidated[0]
								);
								if (!isValid) {
									return { isValid, widgetId: widget.id };
								}
								break;
							case WidgetTypeEnum.AskQuestion:
								isValid = checkIsWidgetDetailValid(
									widget,
									flowConfigurationDetail.ivrFlowType,
									selectedLanguageList,
									widgetItemsToBeValidated[1]
								);
								if (!isValid) {
									return { isValid, widgetId: widget.id };
								}
								break;
							case WidgetTypeEnum.SendMessage:
								isValid = checkIsWidgetDetailValid(
									widget,
									flowConfigurationDetail.ivrFlowType,
									selectedLanguageList,
									widgetItemsToBeValidated[2]
								);
								if (!isValid) {
									return { isValid: false, widgetId: widget.id };
								}
								break;
							case WidgetTypeEnum.LogRequestForHelp:
								if (
									!widget.callbackURL ||
									(widget.isLogTimeoutsEnabled && !widget.timeoutCallbackURL)
								) {
									return { isValid: false, widgetId: widget.id };
								}
								break;
							default:
								break;
						}
					}
				}
				return { isValid: true, widgetId: "" };
			}
		}
	}
	return { isValid: false, widgetId: "" };
};

export type WidgetPropertyType =
	| string
	| ResponseMapModel[]
	| WidgetLanguageDetailModel[]
	| number
	| AudioFileModel
	| AudioFileModel[]
	| boolean;

export interface WizardStepperProp {
	widgetId?: string;
	ivrFlowType: IVRFlowTypeEnum | string;
	highlightRequiredDetail: boolean;
}

export interface IVRFlowStepContent {
	id: string;
	title: string;
	type: string;
	component: React.FC;
	componentProps: WizardStepperProp;
}

export interface IVRContextType {
	state: FlowWizardState;
	dispatch: React.Dispatch<Action>;
}

const getMaxWidgetOrderId = (wizardConfig: IVRFlowConfigurationModel) => {
	let widgetList = Object.values(wizardConfig);
	widgetList = [].concat(...widgetList).filter((e) => !!e);
	return widgetList.length > 0
		? Math.max.apply(
				Math,
				widgetList.map((w) => w.orderId)
		  )
		: 0;
};

const resetMultiLanguageDetail = (
	widget: any,
	propertyList: string[],
	selectedLanguages: string[]
) => {
	propertyList.forEach((property) => {
		if (widget[property]?.length > 0) {
			widget[property] = widget[property].filter((w: any) =>
				selectedLanguages.includes(w.language)
			);
		}
	});
};

//define actions we can preform
export type Action =
	| {
			type: "ADD_NEW_WIDGET";
			payload: {
				widgetType: WidgetTypeEnum;
				widgetTitle: string;
			};
	  }
	| {
			type: "DELETE_WIDGET";
			payload: { id: string };
	  }
	| {
			type: "INITIALIZE_WIZARD_STEPPER_DETAIL";
			payload: {
				flowConfigurationDetail: IVRFlowConfigurationModel;
				rowKey: string;
				partitionKey: string;
				ivrFlowType: IVRFlowTypeEnum | string;
			};
	  }
	| {
			type: "UPDATE_WIDGET_DETAIL";
			payload: {
				widgetId: string;
				widgetType: WidgetTypeEnum;
				propertyName: AskQuestionType | string;
				propertyValue: WidgetPropertyType;
			};
	  }
	| {
			type: "HANDLE_CREATE_WIDGET";
			payload: { isCreateWidget?: boolean };
	  }
	| {
			type: "RESET_HAS_FLOW_CONFIG_CHANGED";
			payload: {};
	  }
	| {
			type: "SET_ACTIVE_STEPPER_ID";
			payload: { id: string };
	  }
	| {
			type: "RESET_LANGUAGE_AUDIO_DETAIL";
			payload: {};
	  };

//define state to store
export type FlowWizardState = {
	wizardConfig: IVRFlowConfigurationModel;
	stepperContent: IVRFlowStepContent[];
	isCreateNewWidget: boolean;
	hasFlowConfigChanged: boolean;
	rowKey: string;
	partitionKey: string;
	ivrFlowType: IVRFlowTypeEnum | string;
	activeStepperId: string;
};

export const useIVRFlowWidgetConfiguration = () => {
	return useReducer<Reducer<FlowWizardState, Action>, undefined>(
		(state, action): FlowWizardState => {
			//reducer needs to return new state based on previous state and action parsed in
			switch (action.type) {
				case "INITIALIZE_WIZARD_STEPPER_DETAIL":
					const wizardStepperDetail = action.payload.flowConfigurationDetail;
					const wizardStepperContent: IVRFlowStepContent[] = [];

					let widgetList = Object.values(wizardStepperDetail);
					widgetList = [].concat(...widgetList).filter((e) => !!e);
					// sort list by orderId
					widgetList = widgetList.sort((a, b) => a.orderId - b.orderId);

					widgetList.forEach((widget) => {
						let stepperContentObj = {} as IVRFlowStepContent;
						// set id, label and type property
						stepperContentObj.id = widget?.id;
						stepperContentObj.title = widget?.label;
						stepperContentObj.type = widget?.type;
						stepperContentObj.componentProps = {
							ivrFlowType: action.payload.ivrFlowType,
							highlightRequiredDetail: false,
						};

						switch (widget?.type) {
							case WidgetTypeEnum.PickLanguage:
								stepperContentObj.component = PickLanguageWidget as React.FC;
								break;
							case WidgetTypeEnum.LogRequestForHelp:
								stepperContentObj.component = LogRequestForHelpWidget as React.FC;
								break;
							case WidgetTypeEnum.SendMessage:
								stepperContentObj.component = SendMessageWidget as React.FC;
								stepperContentObj.componentProps.widgetId = widget?.id;
								break;
							case WidgetTypeEnum.AskQuestion:
								stepperContentObj.component = AskQuestionWidget as React.FC;
								stepperContentObj.componentProps.widgetId = widget?.id;
								break;

							default:
								break;
						}
						wizardStepperContent.push(stepperContentObj);
					});

					return {
						wizardConfig: action.payload.flowConfigurationDetail,
						stepperContent: wizardStepperContent,
						isCreateNewWidget: false,
						hasFlowConfigChanged: false,
						rowKey: action.payload.rowKey,
						partitionKey: action.payload.partitionKey,
						ivrFlowType: action.payload.ivrFlowType,
						activeStepperId: "",
					};
				case "ADD_NEW_WIDGET":
					const wizardConfigTemp = JSON.parse(
						JSON.stringify(state.wizardConfig)
					);
					const stepperContentTemp = [...state.stepperContent];
					// unique widget order id
					const widgetOrderId = getMaxWidgetOrderId(wizardConfigTemp) + 1;
					let stepperContentObject = {};

					switch (action.payload.widgetType) {
						case WidgetTypeEnum.PickLanguage:
							// assign orderId to pickLanguageWidgetModel
							const pickLanguageWidgetModel: PickLanguageWidgetModel = new PickLanguageWidgetModel();
							pickLanguageWidgetModel.responseMap.push(new ResponseMapModel());
							pickLanguageWidgetModel.orderId = widgetOrderId;

							// set stepper content object
							stepperContentObject = {
								id: pickLanguageWidgetModel.id,
								title: action.payload.widgetTitle,
								type: action.payload.widgetType,
								component: PickLanguageWidget as React.FC,
								componentProps: {
									ivrFlowType: state.ivrFlowType,
									highlightRequiredDetail: false,
								},
							};
							wizardConfigTemp.pickLanguageWidgetDetail = pickLanguageWidgetModel;
							break;
						case WidgetTypeEnum.AskQuestion:
							// unique id for AskQuestion Widget
							const askQuestionWidgetId = "AskQuestionWidget_" + widgetOrderId;
							//  assign value to askQuestionWidget Model property
							const askQuestionWidgetDetail: AskQuestionWidgetModel = new AskQuestionWidgetModel();
							askQuestionWidgetDetail.label = action.payload.widgetTitle;
							askQuestionWidgetDetail.id = askQuestionWidgetId;
							askQuestionWidgetDetail.orderId = widgetOrderId;

							stepperContentObject = {
								id: askQuestionWidgetId,
								title: action.payload.widgetTitle,
								type: action.payload.widgetType,
								component: AskQuestionWidget as React.FC,
								componentProps: {
									widgetId: askQuestionWidgetId,
									ivrFlowType: state.ivrFlowType,
									highlightRequiredDetail: false,
								},
							};
							wizardConfigTemp.askQuestionWidgetDetail.push(
								askQuestionWidgetDetail
							);
							break;
						case WidgetTypeEnum.LogRequestForHelp:
							// assign orderId to logRequestForHelpModel
							const logRequestForHelpModel: LogRequestForHelpWidgetModel = new LogRequestForHelpWidgetModel();
							logRequestForHelpModel.orderId = widgetOrderId;

							stepperContentObject = {
								id: logRequestForHelpModel.id,
								title: action.payload.widgetTitle,
								type: action.payload.widgetType,
								component: LogRequestForHelpWidget as React.FC,
								componentProps: {
									ivrFlowType: state.ivrFlowType,
									highlightRequiredDetail: false,
								},
							};
							wizardConfigTemp.logRequestForHelpWidgetDetail = logRequestForHelpModel;
							break;
						case WidgetTypeEnum.SendMessage:
							// unique id for SendMessage Widget
							const sendMessageWidgetId = "SendMessageWidget_" + widgetOrderId;

							const sendMessageModel: SendMessageWidgetModel = new SendMessageWidgetModel();
							sendMessageModel.label = action.payload.widgetTitle;
							sendMessageModel.id = sendMessageWidgetId;
							sendMessageModel.orderId = widgetOrderId;

							stepperContentObject = {
								id: sendMessageWidgetId,
								title: action.payload.widgetTitle,
								type: action.payload.widgetType,
								component: SendMessageWidget as React.FC,
								componentProps: {
									widgetId: sendMessageWidgetId,
									ivrFlowType: state.ivrFlowType,
									highlightRequiredDetail: false,
								},
							};
							wizardConfigTemp.sendMessageWidgetDetail.push(sendMessageModel);
							break;
						default:
							break;
					}
					stepperContentTemp.push(stepperContentObject as IVRFlowStepContent);

					return {
						...state,
						wizardConfig: wizardConfigTemp,
						stepperContent: stepperContentTemp,
						isCreateNewWidget: false,
						hasFlowConfigChanged: true,
					};
				case "DELETE_WIDGET":
					const wizardConfigT: IVRFlowConfigurationModel = {
						...state.wizardConfig,
					};
					const stepperContentT = [...state.stepperContent];
					const index = stepperContentT.findIndex(
						(s) => s.id === action.payload.id
					);
					const type = stepperContentT[index].type;
					// remove widget from stepperContent
					if (index > -1) {
						stepperContentT.splice(index, 1);
					}
					// reset/remove model property value
					switch (type) {
						case WidgetTypeEnum.PickLanguage:
							wizardConfigT.pickLanguageWidgetDetail = null;
							// set errorText, errorAudioFile, successText, successAudioFile as [], if PickLanguageWidget is removed
							if (wizardConfigT.logRequestForHelpWidgetDetail) {
								Object.values(wizardConfigT.logRequestForHelpWidgetDetail)
									.filter((property) => property instanceof Array)
									.map((propertyValue) => (propertyValue.length = 0));
							}
							// set messageAudioFile as [], if PickLanguageWidget is removed
							if (wizardConfigT.sendMessageWidgetDetail?.length > 0) {
								wizardConfigT.sendMessageWidgetDetail.forEach(
									(widgetDetail) => {
										Object.values(widgetDetail)
											.filter((property) => property instanceof Array)
											.map((propertyValue) => (propertyValue.length = 0));
									}
								);
							}
							if (wizardConfigT.askQuestionWidgetDetail.length > 0) {
								wizardConfigT.askQuestionWidgetDetail.forEach((askQuestion) => {
									askQuestion.failureDefaultValue = "";
									askQuestion.failureMessage = [];
									askQuestion.failureAudioFile = [];
								});
							}
							// set value of multiLanguageText and multiLanguageAudio property as [], if PickLanguageWidget is removed
							if (wizardConfigT.askQuestionWidgetDetail?.length > 0) {
								wizardConfigT.askQuestionWidgetDetail.forEach(
									(widgetDetail) => {
										Object.values(widgetDetail)
											.filter((property) => property instanceof Array)
											.map((propertyValue) => (propertyValue.length = 0));
									}
								);
							}
							break;
						case WidgetTypeEnum.AskQuestion:
							const askQuestionIndex = wizardConfigT.askQuestionWidgetDetail.findIndex(
								(askQuestion: AskQuestionWidgetModel) =>
									askQuestion.id === action.payload.id
							);
							if (askQuestionIndex !== -1) {
								wizardConfigT.askQuestionWidgetDetail.splice(
									askQuestionIndex,
									1
								);
							}
							break;
						case WidgetTypeEnum.LogRequestForHelp:
							wizardConfigT.logRequestForHelpWidgetDetail = null;
							break;
						case WidgetTypeEnum.SendMessage:
							const index = wizardConfigT.sendMessageWidgetDetail.findIndex(
								(sendMessageDetail: SendMessageWidgetModel) =>
									sendMessageDetail.id === action.payload.id
							);
							if (index !== -1) {
								wizardConfigT.sendMessageWidgetDetail.splice(index, 1);
							}
							break;
						default:
							break;
					}
					// set nextAction property as null if widgetId to be removed is set in any of the widgets
					const widgetListT = []
						.concat(...Object.values(wizardConfigT))
						.filter((w) => !!w);
					widgetListT.forEach((widget: any) => {
						if (widget.nextAction && widget.nextAction === action.payload.id) {
							widget.nextAction = "";
						} else if (widget.responseMap && widget.responseMap.length > 0) {
							widget.responseMap
								.filter(
									(r: ResponseMapModel) => r.nextAction === action.payload.id
								)
								.map((r: ResponseMapModel) => (r.nextAction = ""));
						}
					});
					return {
						...state,
						wizardConfig: wizardConfigT,
						stepperContent: stepperContentT,
						isCreateNewWidget: false,
						hasFlowConfigChanged: true,
					};
				case "UPDATE_WIDGET_DETAIL":
					const wizardConfigObj: IVRFlowConfigurationModel = {
						...state.wizardConfig,
					};
					const stepperContentList = [...state.stepperContent];
					const selectedLanguageList = GetSelectedLanguageList(
						wizardConfigObj.pickLanguageWidgetDetail
					);
					switch (action.payload.widgetType) {
						case WidgetTypeEnum.PickLanguage:
							(wizardConfigObj.pickLanguageWidgetDetail as any)[
								action.payload.propertyName
							] = action.payload.propertyValue;
							// set value of isValidated property for studio flow
							const isValid = checkIsWidgetDetailValid(
								wizardConfigObj.pickLanguageWidgetDetail,
								state.ivrFlowType,
								selectedLanguageList,
								widgetItemsToBeValidated[0]
							);
							(wizardConfigObj.pickLanguageWidgetDetail as any).isValidated = isValid;
							break;
						case WidgetTypeEnum.AskQuestion:
							const askQuestionIndex = wizardConfigObj.askQuestionWidgetDetail.findIndex(
								(askQuestion: AskQuestionWidgetModel) =>
									askQuestion.id === action.payload.widgetId
							);
							// update failureActionConfiguration values on failureActionType change
							if (askQuestionIndex !== -1) {
								const askQuestionWidgetDetailT =
									wizardConfigObj.askQuestionWidgetDetail[askQuestionIndex];

								(askQuestionWidgetDetailT[
									action.payload.propertyName as AskQuestionType
								] as WidgetPropertyType) = action.payload
									.propertyValue as WidgetPropertyType;
								if (
									askQuestionWidgetDetailT.failureActionType ===
									FailureActionType.UseDefaultValue
								) {
									askQuestionWidgetDetailT.failureMessage = [];
									askQuestionWidgetDetailT.failureAudioFile = [];
								} else if (
									askQuestionWidgetDetailT.failureActionType ===
									FailureActionType.SendMessageAndExit
								) {
									askQuestionWidgetDetailT.failureDefaultValue = "";
								} else if (
									askQuestionWidgetDetailT.failureActionType ===
									FailureActionType.Exit
								) {
									askQuestionWidgetDetailT.failureDefaultValue = "";
									askQuestionWidgetDetailT.failureMessage = [];
									askQuestionWidgetDetailT.failureAudioFile = [];
								}
								askQuestionWidgetDetailT.isVoiceNoteAllowed =
									askQuestionWidgetDetailT.responseMap.length > 0
										? false
										: askQuestionWidgetDetailT.isVoiceNoteAllowed;
								// set value of isValidated property for studio flow
								const isValid = checkIsWidgetDetailValid(
									wizardConfigObj.askQuestionWidgetDetail[askQuestionIndex],
									state.ivrFlowType,
									selectedLanguageList,
									widgetItemsToBeValidated[1]
								);
								wizardConfigObj.askQuestionWidgetDetail[
									askQuestionIndex
								].isValidated = isValid;
							}
							break;
						case WidgetTypeEnum.LogRequestForHelp:
							(wizardConfigObj.logRequestForHelpWidgetDetail as any)[
								action.payload.propertyName
							] = action.payload.propertyValue;
							// set value of isValidated property for studio flow
							wizardConfigObj.logRequestForHelpWidgetDetail!.isValidated =
								wizardConfigObj.logRequestForHelpWidgetDetail?.callbackURL &&
								(wizardConfigObj.logRequestForHelpWidgetDetail
									?.isLogTimeoutsEnabled
									? wizardConfigObj.logRequestForHelpWidgetDetail
											?.timeoutCallbackURL
									: true)
									? true
									: false;
							break;
						case WidgetTypeEnum.SendMessage:
							const index = wizardConfigObj.sendMessageWidgetDetail.findIndex(
								(sendMessageDetail: SendMessageWidgetModel) =>
									sendMessageDetail.id === action.payload.widgetId
							);
							if (index !== -1) {
								const sendMessageDetailT =
									wizardConfigObj.sendMessageWidgetDetail[index];
								(sendMessageDetailT[
									action.payload.propertyName as SendMessageType
								] as WidgetPropertyType) = action.payload
									.propertyValue as WidgetPropertyType;
								// set value of isValidated property for studio flow
								const isValid = checkIsWidgetDetailValid(
									wizardConfigObj.sendMessageWidgetDetail[index],
									state.ivrFlowType,
									selectedLanguageList,
									widgetItemsToBeValidated[2]
								);
								wizardConfigObj.sendMessageWidgetDetail[
									index
								].isValidated = isValid;
							}
							break;
						default:
							break;
					}
					// if propertyName is label, update stepper content label
					if (action.payload.propertyName.toLocaleLowerCase() === "label") {
						const index = stepperContentList.findIndex(
							(s) => s.id === action.payload.widgetId
						);
						stepperContentList[index].title = action.payload
							.propertyValue as string;
					}

					return {
						...state,
						wizardConfig: wizardConfigObj,
						stepperContent: stepperContentList,
						isCreateNewWidget: false,
						hasFlowConfigChanged: true,
					};
				case "HANDLE_CREATE_WIDGET":
					const isCreateNewWidgetTemp =
						action.payload.isCreateWidget === false ? false : true;
					return {
						...state,
						isCreateNewWidget: isCreateNewWidgetTemp,
					};
				case "RESET_HAS_FLOW_CONFIG_CHANGED":
					return {
						...state,
						hasFlowConfigChanged: false,
					};
				case "SET_ACTIVE_STEPPER_ID":
					return { ...state, activeStepperId: action.payload.id };
				case "RESET_LANGUAGE_AUDIO_DETAIL":
					const tempWizardConfig: IVRFlowConfigurationModel = {
						...state.wizardConfig,
					};
					const selectedLanguages = GetSelectedLanguageList(
						tempWizardConfig.pickLanguageWidgetDetail
					);
					const askQuestionPropertyArray: string[] = [
						"questionText",
						"retryMessage",
						"failureMessage",
						"questionAudioFile",
						"retryAudioFile",
						"failureAudioFile",
					];
					const sendMessagePropertyArray: string[] = [
						"messageText",
						"messageAudioFile",
					];
					const logRequestPropertyArray: string[] = [
						"successText",
						"errorText",
						"successAudioFile",
						"errorAudioFile",
					];
					const tempWidgetList = []
						.concat(...Object.values(tempWizardConfig))
						.filter((w) => !!w);
					tempWidgetList.forEach((widget: any) => {
						if (widget.type === WidgetTypeEnum.AskQuestion) {
							resetMultiLanguageDetail(
								widget,
								askQuestionPropertyArray,
								selectedLanguages
							);
						}
						if (widget.type === WidgetTypeEnum.SendMessage) {
							resetMultiLanguageDetail(
								widget,
								sendMessagePropertyArray,
								selectedLanguages
							);
						}
						if (widget.type === WidgetTypeEnum.LogRequestForHelp) {
							resetMultiLanguageDetail(
								widget,
								logRequestPropertyArray,
								selectedLanguages
							);
						}
					});
					return {
						...state,
						wizardConfig: tempWizardConfig,
					};
			}
			assertUnreachable(action);
		},
		undefined,
		() => ({
			wizardConfig: new IVRFlowConfigurationModel(),
			stepperContent: [],
			isCreateNewWidget: false,
			hasFlowConfigChanged: false,
			rowKey: "",
			partitionKey: "",
			ivrFlowType: "",
			activeStepperId: "",
		})
	);
};

// context to share state between child components
export const IVRFlowWizardContext = React.createContext<IVRContextType>({
	state: {} as FlowWizardState,
	dispatch: ({ type, payload }: Action) => {},
});
